import React, { useState, useRef } from 'react';

const API_BASE = (process.env.PUBLIC_URL || '') + '/api';

export default function ZefoImport({ siteId, siteName }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  // Import options
  const [opts, setOpts] = useState({
    keywords: true,
    rankings: true,
    page_scans: true,
    work_log: false,
    analytics: false,
  });

  // Date range
  const [dateMode, setDateMode] = useState('year'); // 'all', 'year', '6m', '3m', 'custom'
  const [customDate, setCustomDate] = useState('');

  function getCutoffDate() {
    const now = new Date();
    switch (dateMode) {
      case 'year':
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().split('T')[0];
      case '6m':
        return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()).toISOString().split('T')[0];
      case '3m':
        return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()).toISOString().split('T')[0];
      case 'custom':
        return customDate || null;
      default:
        return null; // 'all'
    }
  }

  async function handleAnalyze() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_BASE}/sites/${siteId}/zefo/analyze`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function handleImport() {
    if (!file || !preview) return;
    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_keywords', opts.keywords);
    formData.append('import_rankings', opts.rankings);
    formData.append('import_page_scans', opts.page_scans);
    formData.append('import_work_log', opts.work_log);
    formData.append('import_analytics', opts.analytics);

    const cutoff = getCutoffDate();
    if (cutoff) formData.append('date_from', cutoff);

    try {
      const res = await fetch(`${API_BASE}/sites/${siteId}/zefo/import`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Import failed' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data.results);
    } catch (err) {
      setError(err.message);
    }
    setImporting(false);
  }

  function handleFileChange(e) {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(null);
      setResult(null);
      setError(null);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.zip')) {
      setFile(f);
      setPreview(null);
      setResult(null);
      setError(null);
    }
  }

  function toggleOpt(key) {
    setOpts(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const anySelected = Object.values(opts).some(v => v);

  // -----------------------------------------------------------------------

  return (
    <div style={{
      background: '#111827',
      border: '1px solid #1E293B',
      borderRadius: '10px',
      padding: '24px',
      marginBottom: '16px',
    }}>
      <div style={{
        fontSize: '16px',
        fontWeight: 700,
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: 'rgba(168,85,247,0.15)',
          fontSize: '14px',
        }}>Z</span>
        ייבוא מ-ZEFO
      </div>
      <p style={{ fontSize: '13px', color: '#8B99AE', marginBottom: '20px' }}>
        העלה קובץ גיבוי (.zip) מ-ZEFO לייבוא ביטויים, היסטוריית מיקומים ונתונים נוספים
      </p>

      {/* File Upload Zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
        style={{
          border: '2px dashed ' + (file ? '#3B82F6' : '#1E293B'),
          borderRadius: '8px',
          padding: '28px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: file ? 'rgba(59,130,246,0.05)' : 'transparent',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {file ? (
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
              {file.name}
            </div>
            <div style={{ fontSize: '12px', color: '#8B99AE' }}>
              {(file.size / 1024 / 1024).toFixed(2)} MB - לחץ להחלפה
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.3 }}>&#128230;</div>
            <div style={{ fontSize: '14px', color: '#8B99AE' }}>
              גרור קובץ ZIP לכאן או לחץ לבחירה
            </div>
          </div>
        )}
      </div>

      {/* Analyze button */}
      {file && !preview && !result && (
        <button
          onClick={handleAnalyze}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '6px',
            border: 'none',
            background: '#3B82F6',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: "'Heebo', sans-serif",
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'מנתח קובץ...' : 'ניתוח קובץ'}
        </button>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px',
          padding: '10px 16px',
          marginTop: '12px',
          fontSize: '13px',
          color: '#EF4444',
        }}>
          {error}
        </div>
      )}

      {/* Preview Results */}
      {preview && !result && (
        <div style={{ marginTop: '16px' }}>
          {/* Domain match warning */}
          {!preview.domain_match && (
            <div style={{
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: '8px',
              padding: '10px 16px',
              marginBottom: '12px',
              fontSize: '13px',
              color: '#F59E0B',
            }}>
              שים לב: הדומיין בקובץ ({preview.domain}) לא תואם לאתר ({preview.site_url})
            </div>
          )}

          {/* Module Checkboxes */}
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            marginBottom: '10px',
          }}>
            מה לייבא?
          </div>

          {[
            {
              key: 'keywords',
              label: 'ביטויים',
              detail: `${preview.modules.keywords.new} חדשים, ${preview.modules.keywords.existing} קיימים`,
              count: preview.modules.keywords.total,
              color: '#3B82F6',
              recommended: true,
            },
            {
              key: 'rankings',
              label: 'היסטוריית מיקומים',
              detail: `${preview.modules.rankings.date_from || '?'} עד ${preview.modules.rankings.date_to || '?'}`,
              count: preview.modules.rankings.total_records,
              color: '#22C55E',
              recommended: true,
            },
            {
              key: 'page_scans',
              label: 'סריקות עמודים',
              detail: 'סנאפשוט אחרון לכל עמוד',
              count: preview.modules.page_scans.total_pages,
              color: '#22D3EE',
              recommended: true,
            },
            {
              key: 'work_log',
              label: 'היסטוריית עבודה',
              detail: 'פעולות שבוצעו באתר',
              count: preview.modules.work_log.total_entries,
              color: '#F59E0B',
              recommended: false,
            },
            {
              key: 'analytics',
              label: 'נתוני תנועה',
              detail: preview.modules.analytics.date_from
                ? `${preview.modules.analytics.date_from} עד ${preview.modules.analytics.date_to}`
                : 'אין נתונים',
              count: preview.modules.analytics.total_records,
              color: '#A855F7',
              recommended: false,
            },
          ].map(mod => (
            <div
              key={mod.key}
              onClick={() => mod.count > 0 && toggleOpt(mod.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                borderRadius: '8px',
                border: '1px solid ' + (opts[mod.key] ? 'rgba(59,130,246,0.4)' : '#1E293B'),
                background: opts[mod.key] ? 'rgba(59,130,246,0.05)' : 'transparent',
                marginBottom: '6px',
                cursor: mod.count > 0 ? 'pointer' : 'not-allowed',
                opacity: mod.count > 0 ? 1 : 0.4,
                transition: 'all 0.15s',
              }}
            >
              {/* Checkbox */}
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                border: '2px solid ' + (opts[mod.key] ? '#3B82F6' : '#4B5C73'),
                background: opts[mod.key] ? '#3B82F6' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}>
                {opts[mod.key] && (
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: 800 }}>&#10003;</span>
                )}
              </div>

              {/* Label */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  {mod.label}
                  {mod.recommended && (
                    <span style={{
                      fontSize: '10px',
                      color: '#22C55E',
                      background: 'rgba(34,197,94,0.15)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      marginRight: '6px',
                      fontWeight: 500,
                    }}>
                      מומלץ
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#8B99AE', marginTop: '2px' }}>
                  {mod.detail}
                </div>
              </div>

              {/* Count badge */}
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '13px',
                fontWeight: 600,
                color: mod.color,
                background: mod.color + '15',
                padding: '3px 10px',
                borderRadius: '6px',
                minWidth: '50px',
                textAlign: 'center',
              }}>
                {mod.count.toLocaleString()}
              </div>
            </div>
          ))}

          {/* Date Range - only show if rankings or work_log or analytics is selected */}
          {(opts.rankings || opts.work_log || opts.analytics) && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: '#0B0F1A',
              borderRadius: '8px',
              border: '1px solid #1E293B',
            }}>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>
                תקופת ייבוא
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: 'הכל' },
                  { key: 'year', label: 'שנה אחרונה' },
                  { key: '6m', label: '6 חודשים' },
                  { key: '3m', label: '3 חודשים' },
                  { key: 'custom', label: 'תאריך מותאם' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setDateMode(opt.key)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '6px',
                      border: '1px solid ' + (dateMode === opt.key ? '#3B82F6' : '#1E293B'),
                      background: dateMode === opt.key ? 'rgba(59,130,246,0.15)' : 'transparent',
                      color: dateMode === opt.key ? '#3B82F6' : '#8B99AE',
                      fontSize: '13px',
                      fontFamily: "'Heebo', sans-serif",
                      fontWeight: dateMode === opt.key ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {dateMode === 'custom' && (
                <input
                  type="date"
                  value={customDate}
                  onChange={e => setCustomDate(e.target.value)}
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #1E293B',
                    background: '#111827',
                    color: '#E2E8F0',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              )}
              {dateMode !== 'all' && (
                <div style={{ fontSize: '12px', color: '#8B99AE', marginTop: '8px' }}>
                  יייובא נתונים מ-{getCutoffDate()} ואילך
                </div>
              )}
            </div>
          )}

          {/* Import Button */}
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleImport}
              disabled={importing || !anySelected}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '6px',
                border: 'none',
                background: anySelected ? '#22C55E' : '#4B5C73',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: "'Heebo', sans-serif",
                cursor: (importing || !anySelected) ? 'not-allowed' : 'pointer',
                opacity: importing ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
            >
              {importing ? 'מייבא נתונים...' : 'ייבוא'}
            </button>
            <button
              onClick={() => {
                setFile(null);
                setPreview(null);
                setResult(null);
                setError(null);
              }}
              style={{
                padding: '12px 20px',
                borderRadius: '6px',
                border: '1px solid #1E293B',
                background: 'transparent',
                color: '#8B99AE',
                fontSize: '14px',
                fontFamily: "'Heebo', sans-serif",
                cursor: 'pointer',
              }}
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* Import Results */}
      {result && (
        <div style={{ marginTop: '16px' }}>
          <div style={{
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '12px',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#22C55E', marginBottom: '8px' }}>
              &#10003; הייבוא הושלם בהצלחה
            </div>

            {result.keywords && (
              <ResultLine
                label="ביטויים"
                value={`${result.keywords.added} חדשים, ${result.keywords.updated} עודכנו, ${result.keywords.skipped} ללא שינוי`}
              />
            )}
            {result.rankings && (
              <ResultLine
                label="מיקומים"
                value={`${result.rankings.imported.toLocaleString()} רשומות יובאו${result.rankings.skipped > 0 ? `, ${result.rankings.skipped.toLocaleString()} דולגו` : ''}`}
              />
            )}
            {result.page_scans && (
              <ResultLine
                label="סריקות"
                value={`${result.page_scans.imported} עמודים`}
              />
            )}
            {result.work_log && (
              <ResultLine
                label="היסטוריית עבודה"
                value={`${result.work_log.imported} רשומות`}
              />
            )}
            {result.analytics && (
              <ResultLine
                label="תנועה"
                value={`${result.analytics.imported.toLocaleString()} רשומות`}
              />
            )}
          </div>

          <button
            onClick={() => {
              setFile(null);
              setPreview(null);
              setResult(null);
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #1E293B',
              background: '#1A2237',
              color: '#E2E8F0',
              fontSize: '13px',
              fontFamily: "'Heebo', sans-serif",
              cursor: 'pointer',
            }}
          >
            סיום
          </button>
        </div>
      )}
    </div>
  );
}


function ResultLine({ label, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      fontSize: '13px',
    }}>
      <span style={{ color: '#8B99AE' }}>{label}</span>
      <span style={{ color: '#E2E8F0', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>
        {value}
      </span>
    </div>
  );
}